import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, Globe, Tag, Pencil, Lock, ShieldAlert, Download, Upload, Database } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  usePageTitle("Settings");
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminPasscode, setAdminPasscode] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [configType, setConfigType] = useState("standard"); // "standard" or "dealer"

  const [sourceMediums, setSourceMediums] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newSourceMedium, setNewSourceMedium] = useState({
    source: "",
    medium: "",
    channel: "",
  });
  const [newBrand, setNewBrand] = useState("");

  // Edit source/medium modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({
    source: "",
    medium: "",
    channel: "",
  });

  // Edit brand modal state
  const [editBrandModalOpen, setEditBrandModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editBrandName, setEditBrandName] = useState("");

  // Passcode change state
  const [passcodeModalOpen, setPasscodeModalOpen] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState("");
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");

  // Data management state
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [clearBeforeImport, setClearBeforeImport] = useState(false);

  // Check if admin is already authenticated in session
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("utm_admin_authenticated");
    if (adminAuth === "true") {
      setIsAdminAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAdminAuthenticated) {
      fetchData();
    }
  }, [isAdminAuthenticated, configType]);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminPasscode.trim()) {
      toast.error("Please enter the admin passcode");
      return;
    }

    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-admin`, { passcode: adminPasscode });
      if (response.data.success) {
        sessionStorage.setItem("utm_admin_authenticated", "true");
        sessionStorage.setItem("utm_admin_passcode", adminPasscode); // Store for export/import
        setIsAdminAuthenticated(true);
        toast.success("Admin access granted");
      }
    } catch (error) {
      toast.error("Invalid admin passcode");
    } finally {
      setAuthLoading(false);
      setAdminPasscode("");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [smResponse, brandsResponse] = await Promise.all([
        axios.get(`${API}/settings/source-mediums?config_type=${configType}`),
        axios.get(`${API}/settings/brands?config_type=${configType}`),
      ]);
      setSourceMediums(smResponse.data);
      setBrands(brandsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  // Source/Medium handlers
  const addSourceMedium = async (e) => {
    e.preventDefault();
    if (!newSourceMedium.source || !newSourceMedium.medium || !newSourceMedium.channel) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/settings/source-mediums`, { ...newSourceMedium, config_type: configType });
      toast.success("Source/Medium added");
      setNewSourceMedium({ source: "", medium: "", channel: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add");
    }
  };

  const openEditModal = (item) => {
    setEditingItem(item);
    setEditForm({
      source: item.source,
      medium: item.medium,
      channel: item.channel,
    });
    setEditModalOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.source || !editForm.medium || !editForm.channel) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.put(`${API}/settings/source-mediums/${editingItem.id}`, editForm);
      toast.success("Updated successfully");
      setEditModalOpen(false);
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const deleteSourceMedium = async (id) => {
    try {
      await axios.delete(`${API}/settings/source-mediums/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Brand handlers
  const addBrand = async (e) => {
    e.preventDefault();
    if (!newBrand.trim()) {
      toast.error("Please enter a brand name");
      return;
    }

    try {
      await axios.post(`${API}/settings/brands`, { name: newBrand, config_type: configType });
      toast.success("Brand added");
      setNewBrand("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add");
    }
  };

  const openEditBrandModal = (brand) => {
    setEditingBrand(brand);
    setEditBrandName(brand.name);
    setEditBrandModalOpen(true);
  };

  const saveBrandEdit = async () => {
    if (!editBrandName.trim()) {
      toast.error("Please enter a brand name");
      return;
    }

    try {
      await axios.put(`${API}/settings/brands/${editingBrand.id}`, { name: editBrandName });
      toast.success("Brand updated successfully");
      setEditBrandModalOpen(false);
      setEditingBrand(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update brand");
    }
  };

  const deleteBrand = async (id) => {
    try {
      await axios.delete(`${API}/settings/brands/${id}`);
      toast.success("Deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Passcode handlers
  const changePasscode = async () => {
    if (!currentPasscode || !newPasscode || !confirmPasscode) {
      toast.error("Please fill in all fields");
      return;
    }

    if (newPasscode !== confirmPasscode) {
      toast.error("New passcodes do not match");
      return;
    }

    if (newPasscode.length < 4) {
      toast.error("Passcode must be at least 4 characters");
      return;
    }

    try {
      await axios.post(`${API}/settings/change-passcode`, {
        current_passcode: currentPasscode,
        new_passcode: newPasscode,
      });
      toast.success("Passcode changed successfully");
      setPasscodeModalOpen(false);
      setCurrentPasscode("");
      setNewPasscode("");
      setConfirmPasscode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change passcode");
    }
  };

  // Data export handler
  const handleExport = async () => {
    const storedPasscode = sessionStorage.getItem("utm_admin_passcode");
    if (!storedPasscode) {
      toast.error("Admin session expired. Please re-authenticate.");
      setIsAdminAuthenticated(false);
      sessionStorage.removeItem("utm_admin_authenticated");
      return;
    }

    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/admin/export-all?passcode=${storedPasscode}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `agco_utm_builder_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Data exported successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export data");
    } finally {
      setExportLoading(false);
    }
  };

  // Data import handler
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file to import");
      return;
    }

    const storedPasscode = sessionStorage.getItem("utm_admin_passcode");
    if (!storedPasscode) {
      toast.error("Admin session expired. Please re-authenticate.");
      setIsAdminAuthenticated(false);
      sessionStorage.removeItem("utm_admin_authenticated");
      return;
    }

    setImportLoading(true);
    try {
      const fileContent = await importFile.text();
      const importData = JSON.parse(fileContent);
      
      const response = await axios.post(
        `${API}/admin/import-all?passcode=${storedPasscode}&clear_existing=${clearBeforeImport}`,
        importData
      );
      
      toast.success(response.data.message || "Data imported successfully!");
      setImportFile(null);
      fetchData(); // Refresh the settings data
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON file");
      } else {
        toast.error(error.response?.data?.detail || "Failed to import data");
      }
    } finally {
      setImportLoading(false);
    }
  };

  // Render restricted access screen
  if (!isAdminAuthenticated) {
    return (
      <div className="p-8" data-testid="settings-page">
        <div className="max-w-md mx-auto mt-16">
          {/* Restricted Banner */}
          <div className="bg-[#AE132A] text-white p-6 mb-8">
            <div className="flex items-center gap-3 mb-3">
              <ShieldAlert className="w-6 h-6" />
              <h2 className="text-lg font-bold uppercase tracking-wider">Restricted Access</h2>
            </div>
            <p className="text-white/80 text-sm">
              This page contains administrative settings and is only accessible to authorized administrators.
            </p>
          </div>

          {/* Admin Login Form */}
          <div className="border border-zinc-200 bg-white p-8">
            <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-6">
              Admin Authentication
            </h3>
            <form onSubmit={handleAdminLogin} className="space-y-6">
              <div>
                <label className="block text-xs text-zinc-500 mb-2">Admin Passcode</label>
                <div className="relative">
                  <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    type="password"
                    value={adminPasscode}
                    onChange={(e) => setAdminPasscode(e.target.value)}
                    placeholder="Enter admin passcode"
                    className="input-with-icon"
                    data-testid="admin-passcode-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={authLoading}
                className="btn-primary w-full"
                data-testid="admin-login-btn"
              >
                {authLoading ? "Verifying..." : "Access Settings"}
              </Button>
            </form>
            <p className="text-xs text-zinc-400 mt-6 text-center">
              Contact <a href="mailto:sarthak.madaan@agcocorp.com" className="text-[#AE132A] hover:underline">sarthak.madaan@agcocorp.com</a> if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-950 red-thread pb-3">
            Settings
          </h1>
          <p className="text-sm text-zinc-500 mt-4">
            Configure UTM sources, mediums, GA4 channels, and brands
          </p>
        </div>
        <Button
          onClick={() => setPasscodeModalOpen(true)}
          className="btn-secondary"
          data-testid="change-passcode-btn"
        >
          <Lock className="w-4 h-4 mr-2" />
          Change Passcode
        </Button>
      </div>

      {/* Config Type Selector */}
      <div className="mb-6 flex items-center gap-4">
        <span className="text-sm font-medium text-zinc-600">Configuration for:</span>
        <div className="flex gap-2">
          <Button
            variant={configType === "standard" ? "default" : "outline"}
            size="sm"
            onClick={() => setConfigType("standard")}
            className={configType === "standard" ? "bg-[#AE132A] hover:bg-[#8A0F20]" : "rounded-none"}
            data-testid="config-standard-btn"
          >
            Standard Links
          </Button>
          <Button
            variant={configType === "dealer" ? "default" : "outline"}
            size="sm"
            onClick={() => setConfigType("dealer")}
            className={configType === "dealer" ? "bg-[#AE132A] hover:bg-[#8A0F20]" : "rounded-none"}
            data-testid="config-dealer-btn"
          >
            Dealer Redirect
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading settings...</div>
      ) : (
        <Tabs defaultValue="sources" className="space-y-6">
          <TabsList className="bg-zinc-100 p-1 rounded-none">
            <TabsTrigger
              value="sources"
              className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
              data-testid="sources-tab"
            >
              <Globe className="w-4 h-4 mr-2" />
              Sources & Mediums
            </TabsTrigger>
            <TabsTrigger
              value="brands"
              className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
              data-testid="brands-tab"
            >
              <Tag className="w-4 h-4 mr-2" />
              Brands
            </TabsTrigger>
            <TabsTrigger
              value="data"
              className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#AE132A] uppercase text-xs tracking-widest font-medium px-6"
              data-testid="data-tab"
            >
              <Database className="w-4 h-4 mr-2" />
              Data Management
            </TabsTrigger>
          </TabsList>

          {/* Sources & Mediums Tab */}
          <TabsContent value="sources" className="space-y-6">
            {/* Add Form */}
            <div className="border border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                Add New Source/Medium
              </h3>
              <form onSubmit={addSourceMedium} className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-2">Source</label>
                  <Input
                    type="text"
                    value={newSourceMedium.source}
                    onChange={(e) =>
                      setNewSourceMedium((prev) => ({
                        ...prev,
                        source: e.target.value.toLowerCase(),
                      }))
                    }
                    placeholder="google"
                    className="rounded-none h-10"
                    data-testid="new-source-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-2">Medium</label>
                  <Input
                    type="text"
                    value={newSourceMedium.medium}
                    onChange={(e) =>
                      setNewSourceMedium((prev) => ({
                        ...prev,
                        medium: e.target.value.toLowerCase(),
                      }))
                    }
                    placeholder="cpc"
                    className="rounded-none h-10"
                    data-testid="new-medium-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-2">GA4 Channel</label>
                  <Input
                    type="text"
                    value={newSourceMedium.channel}
                    onChange={(e) =>
                      setNewSourceMedium((prev) => ({
                        ...prev,
                        channel: e.target.value,
                      }))
                    }
                    placeholder="Paid Search"
                    className="rounded-none h-10"
                    data-testid="new-channel-input"
                  />
                </div>
                <Button type="submit" className="btn-primary h-10" data-testid="add-source-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </form>
            </div>

            {/* Table */}
            <div className="border border-zinc-200 bg-white" data-testid="sources-table">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-zinc-100">
                    <TableHead className="font-semibold">Source</TableHead>
                    <TableHead className="font-semibold">Medium</TableHead>
                    <TableHead className="font-semibold">GA4 Channel</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sourceMediums.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-zinc-500">
                        No source/medium pairs configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    sourceMediums.map((item) => (
                      <TableRow key={item.id} className="hover:bg-zinc-50">
                        <TableCell className="font-medium">{item.source}</TableCell>
                        <TableCell>{item.medium}</TableCell>
                        <TableCell>
                          <span className="inline-block px-2 py-1 text-xs font-medium bg-[#AE132A]/10 text-[#AE132A]">
                            {item.channel}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditModal(item)}
                              className="h-8 w-8 p-0 hover:text-[#AE132A]"
                              data-testid={`edit-source-${item.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteSourceMedium(item.id)}
                              className="h-8 w-8 p-0 hover:text-red-600"
                              data-testid={`delete-source-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Brands Tab */}
          <TabsContent value="brands" className="space-y-6">
            {/* Add Form */}
            <div className="border border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                Add New Brand
              </h3>
              <form onSubmit={addBrand} className="flex gap-4 items-end">
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-2">Brand Name</label>
                  <Input
                    type="text"
                    value={newBrand}
                    onChange={(e) => setNewBrand(e.target.value)}
                    placeholder="Brand Name"
                    className="rounded-none h-10"
                    data-testid="new-brand-input"
                  />
                </div>
                <Button type="submit" className="btn-primary h-10" data-testid="add-brand-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Brand
                </Button>
              </form>
            </div>

            {/* Table */}
            <div className="border border-zinc-200 bg-white" data-testid="brands-table">
              <Table>
                <TableHeader>
                  <TableRow className="table-header hover:bg-zinc-100">
                    <TableHead className="font-semibold">Brand Name</TableHead>
                    <TableHead className="font-semibold">Created</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {brands.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-zinc-500">
                        No brands configured
                      </TableCell>
                    </TableRow>
                  ) : (
                    brands.map((brand) => (
                      <TableRow key={brand.id} className="hover:bg-zinc-50">
                        <TableCell className="font-medium">{brand.name}</TableCell>
                        <TableCell className="text-zinc-500 text-sm">
                          {new Date(brand.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditBrandModal(brand)}
                              className="h-8 w-8 p-0 hover:text-[#AE132A]"
                              data-testid={`edit-brand-${brand.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteBrand(brand.id)}
                              className="h-8 w-8 p-0 hover:text-red-600"
                              data-testid={`delete-brand-${brand.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Data Management Tab */}
          <TabsContent value="data" className="space-y-6">
            {/* Export Section */}
            <div className="border border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                Export All Data
              </h3>
              <p className="text-sm text-zinc-600 mb-4">
                Download all UTM links, dealer links, ITG links, and settings as a JSON file. 
                Use this for backup or migration to a new account.
              </p>
              <Button
                onClick={handleExport}
                disabled={exportLoading}
                className="btn-primary"
                data-testid="export-data-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exportLoading ? "Exporting..." : "Export All Data"}
              </Button>
            </div>

            {/* Import Section */}
            <div className="border border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                Import Data
              </h3>
              <p className="text-sm text-zinc-600 mb-4">
                Upload a previously exported JSON file to restore or migrate data.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-2">Select JSON File</label>
                  <Input
                    type="file"
                    accept=".json"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="rounded-none"
                    data-testid="import-file-input"
                  />
                  {importFile && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Selected: {importFile.name}
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="clearBeforeImport"
                    checked={clearBeforeImport}
                    onChange={(e) => setClearBeforeImport(e.target.checked)}
                    className="rounded border-zinc-300"
                    data-testid="clear-before-import-checkbox"
                  />
                  <label htmlFor="clearBeforeImport" className="text-sm text-zinc-600">
                    Clear existing data before import (replaces all data)
                  </label>
                </div>

                <Button
                  onClick={handleImport}
                  disabled={importLoading || !importFile}
                  className="btn-secondary"
                  data-testid="import-data-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importLoading ? "Importing..." : "Import Data"}
                </Button>
              </div>
            </div>

            {/* Info Section */}
            <div className="border border-amber-200 bg-amber-50 p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-amber-700 mb-2">
                Migration Guide
              </h3>
              <ol className="text-sm text-amber-800 space-y-2 list-decimal list-inside">
                <li>Export your data using the button above</li>
                <li>Save the JSON file to your computer</li>
                <li>In your new account, go to Settings → Data Management</li>
                <li>Upload the JSON file and click Import</li>
              </ol>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* Edit Source/Medium Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Edit Source/Medium</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Source</label>
              <Input
                type="text"
                value={editForm.source}
                onChange={(e) => setEditForm((prev) => ({ ...prev, source: e.target.value.toLowerCase() }))}
                className="rounded-none h-10"
                data-testid="edit-source-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Medium</label>
              <Input
                type="text"
                value={editForm.medium}
                onChange={(e) => setEditForm((prev) => ({ ...prev, medium: e.target.value.toLowerCase() }))}
                className="rounded-none h-10"
                data-testid="edit-medium-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2">GA4 Channel</label>
              <Input
                type="text"
                value={editForm.channel}
                onChange={(e) => setEditForm((prev) => ({ ...prev, channel: e.target.value }))}
                className="rounded-none h-10"
                data-testid="edit-channel-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={saveEdit}
              className="btn-primary"
              data-testid="save-edit-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Modal */}
      <Dialog open={editBrandModalOpen} onOpenChange={setEditBrandModalOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Edit Brand</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Brand Name</label>
              <Input
                type="text"
                value={editBrandName}
                onChange={(e) => setEditBrandName(e.target.value)}
                className="rounded-none h-10"
                data-testid="edit-brand-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditBrandModalOpen(false)}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={saveBrandEdit}
              className="btn-primary"
              data-testid="save-brand-edit-btn"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Passcode Modal */}
      <Dialog open={passcodeModalOpen} onOpenChange={setPasscodeModalOpen}>
        <DialogContent className="rounded-none">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">Change Team Passcode</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Current Passcode</label>
              <Input
                type="password"
                value={currentPasscode}
                onChange={(e) => setCurrentPasscode(e.target.value)}
                placeholder="Enter current passcode"
                className="rounded-none h-10"
                data-testid="current-passcode-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2">New Passcode</label>
              <Input
                type="password"
                value={newPasscode}
                onChange={(e) => setNewPasscode(e.target.value)}
                placeholder="Enter new passcode"
                className="rounded-none h-10"
                data-testid="new-passcode-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2">Confirm New Passcode</label>
              <Input
                type="password"
                value={confirmPasscode}
                onChange={(e) => setConfirmPasscode(e.target.value)}
                placeholder="Confirm new passcode"
                className="rounded-none h-10"
                data-testid="confirm-passcode-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasscodeModalOpen(false);
                setCurrentPasscode("");
                setNewPasscode("");
                setConfirmPasscode("");
              }}
              className="rounded-none"
            >
              Cancel
            </Button>
            <Button
              onClick={changePasscode}
              className="btn-primary"
              data-testid="save-passcode-btn"
            >
              Change Passcode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
